import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  ArrowLeft, ShieldCheck, Download, Lock, BellRing, MessagesSquare, Radio, Loader2,
} from "lucide-react";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SIGNALS } from "@/constants/testIds";
import { SEVERITY } from "@/lib/alerts";
import { SOURCE_LABEL, fmtNum } from "@/lib/signals";
import api from "@/lib/api";

function abs(ts) {
  try { return format(new Date(ts), "MMM d, yyyy · HH:mm"); } catch { return "—"; }
}

const SOURCE_ICON = { alert: BellRing, chat: MessagesSquare, manual: Radio };

function Meta({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-border/40 py-2 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}

export default function RegistryEntry() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/registry/${id}`);
      setR(data);
    } catch {
      setNotFound(true);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/registry/${id}/export.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `signal-${id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setExporting(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-background">
        <WorkspaceNav />
        <div className="px-6 py-20 text-center text-sm text-muted-foreground">Registry record not found.</div>
      </div>
    );
  }

  if (!r) {
    return (
      <div className="min-h-screen bg-background">
        <WorkspaceNav />
        <div className="px-6 py-20 text-center text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  const sev = SEVERITY[r.severity] || SEVERITY.low;
  const ev = r.evidence_snapshot || {};
  const rule = r.rule_version_snapshot || {};
  const SourceIcon = SOURCE_ICON[r.source] || Radio;

  return (
    <div data-testid={SIGNALS.entryRoot} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <main className="mx-auto w-full max-w-5xl px-6 py-8">
        <Link to="/signals/registry" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> Registry
        </Link>

        <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.16em] text-emerald-400">
              <Lock className="h-3 w-3" /> Immutable record
            </div>
            <h1 className="mt-2 flex items-center gap-2 text-2xl font-bold tracking-tight sm:text-3xl">
              <ShieldCheck className="h-6 w-6 text-emerald-400" /> {r.name}
            </h1>
            <p className="mt-1.5 text-sm text-muted-foreground">{r.summary}</p>
          </div>
          <Button data-testid={SIGNALS.exportPdf} onClick={exportPdf} disabled={exporting}
            className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90">
            {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
            Export PDF
          </Button>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="space-y-8 lg:col-span-2">
            <section>
              <h2 className="mb-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Evidence (frozen at detection)</h2>
              <div className="rounded-xl border border-border/60 bg-card/30 p-4">
                {ev.chart_png_b64 ? (
                  <img src={`data:image/png;base64,${ev.chart_png_b64}`} alt="evidence"
                    className="w-full rounded-md border border-border/50" />
                ) : (
                  <div className="py-10 text-center text-sm text-muted-foreground">No chart was captured.</div>
                )}
                <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                  <div className="rounded-lg border border-border/50 bg-card/40 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Value</div>
                    <div className="mt-0.5">{fmtNum(ev.value)}</div>
                  </div>
                  <div className="rounded-lg border border-border/50 bg-card/40 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Baseline</div>
                    <div className="mt-0.5">{fmtNum(ev.baseline)}</div>
                  </div>
                  <div className="rounded-lg border border-border/50 bg-card/40 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Deviation</div>
                    <div className="mt-0.5">{fmtNum(ev.deviation)}</div>
                  </div>
                </div>
                {ev.sql && (
                  <pre className="mt-4 overflow-auto rounded-lg border border-border/50 bg-background/60 p-3 text-[11px] text-muted-foreground">{ev.sql}</pre>
                )}
              </div>
            </section>

            <section>
              <h2 className="mb-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Review thread (snapshot)</h2>
              <div className="rounded-xl border border-border/60 bg-card/30">
                {(r.comments_snapshot || []).length ? (
                  (r.comments_snapshot || []).map((c, i) => (
                    <div key={i} className="border-b border-border/40 px-4 py-3 last:border-0">
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="font-medium text-foreground/90">{c.author_name}</span>
                        <span className="text-muted-foreground">{abs(c.created_at)}</span>
                      </div>
                      <p className="mt-1 text-xs text-foreground/80">{c.body}</p>
                    </div>
                  ))
                ) : <div className="px-4 py-8 text-center text-sm text-muted-foreground">No comments were recorded.</div>}
              </div>
            </section>
          </div>

          <div className="space-y-4">
            <h2 className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Approval lineage</h2>
            <div className="space-y-1 rounded-xl border border-border/60 bg-card/40 p-4 text-sm">
              <Meta label="Severity" value={<span className="inline-flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${sev.dot}`} />{sev.label}</span>} />
              <Meta label="Source" value={<span className="inline-flex items-center gap-1.5"><SourceIcon className="h-3.5 w-3.5 text-accent" />{SOURCE_LABEL[r.source] || r.source}</span>} />
              <Meta label="Detected" value={<span className="text-xs">{abs(r.detected_at)}</span>} />
              <Meta label="Approved" value={<span className="text-xs">{abs(r.approved_at)}</span>} />
              <Meta label="Approved by" value={<span className="text-xs">{r.approved_by_name}</span>} />
              <Meta label="Registry ID" value={<span className="font-mono text-[10px] text-muted-foreground">{r.id}</span>} />
            </div>

            {rule.method && (
              <div className="rounded-xl border border-border/60 bg-card/40 p-4 text-sm">
                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Rule snapshot</div>
                <div className="mt-2 space-y-1 text-xs">
                  <Meta label="Method" value={<Badge variant="outline" className="text-[10px] text-accent border-accent/30">{rule.method}</Badge>} />
                  {rule.cron && <Meta label="Schedule" value={<span className="font-mono text-[10px]">{rule.cron} · {rule.timezone}</span>} />}
                </div>
              </div>
            )}

            {r.source === "alert" && r.alert_id && (
              <Link to={`/alerts/${r.alert_id}`} className="inline-flex items-center gap-1.5 text-xs text-accent hover:underline">
                <BellRing className="h-3.5 w-3.5" /> View source alert
              </Link>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
